import React, { useEffect, useRef, useState } from 'react';
// import '../../assets/styles/auth-tokens.css';
// import '../../assets/styles/menu.css';

const defaultFilter = (item, query) =>
  String(item.label ?? '').toLowerCase().includes(query.toLowerCase());

// Generic dropdown menu: a trigger you control, a popover panel, and an
// optional search box that filters `items`. Not phone-specific — anything
// that needs "click a thing, pick from a searchable list" can reuse this.
const Menu = ({
  trigger,
  items = [],
  getKey = (item, index) => item.value ?? index,
  renderItem = (item) => item.label,
  onSelect,
  searchable = false,
  searchPlaceholder = 'Search',
  filterItem = defaultFilter,
  emptyText = 'No results found',
  align = 'left',
  className = '',
  closeOnSelect = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const filtered = searchable && query
    ? items.filter((item) => filterItem(item, query))
    : items;

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlightedIndex(0);
      if (searchable) {
        // Let the panel mount before focusing.
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    }
  }, [open, searchable]);

  const handleSelect = (item) => {
    onSelect && onSelect(item);
    if (closeOnSelect) setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlightedIndex]) handleSelect(filtered[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {trigger({ open, toggle: () => setOpen((o) => !o), close: () => setOpen(false) })}

      {open && (
        <div className={`absolute z-50 mt-2 bg-white border border-slate-200 shadow-sm rounded-xl w-64 max-h-80 flex flex-col overflow-hidden ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {searchable && (
            <div className="flex items-center px-3 py-3 border-b border-slate-100 bg-slate-50">
              <i className="icofont-search text-slate-400 mr-2 text-lg"></i>
              
              <input
                ref={searchInputRef}
                className="w-full bg-transparent outline-none text-sm text-slate-700"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlightedIndex(0);
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
              />
            </div>
          )}
          <ul className="overflow-y-auto w-full py-1" role="listbox">
            {filtered.length === 0 ? (
              <li className="px-4 py-4 text-sm text-slate-500 text-center">{emptyText}</li>
            ) : (
              filtered.map((item, index) => (
                <li
                  key={getKey(item, index)}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  className={`px-4 py-2 cursor-pointer transition-colors text-sm flex items-center ${index === highlightedIndex ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleSelect(item)}
                >
                  {renderItem(item, { active: index === highlightedIndex })}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Menu;
